import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useCreateContentItem } from '../../hooks/backend/marketing'
import { writeErrorMessage } from '../../utils/writeError'
import type { CodeLabel, MarketingPerson, MarketingPlan } from '../../utils/marketing'

/**
 * A new piece of content.
 *
 * It lands in Draft, and the dialog says so. The task board learned this the
 * hard way on 23 August: pressing Add on the Ongoing heading manufactured a card
 * whose group and status disagreed, because the dialog did not say where the row
 * was going. A create that lands somewhere other than where the person is
 * standing has to announce it.
 */
export function NewContentDialog({
  open,
  onOpenChange,
  channels,
  formats,
  audiences,
  ctas,
  plans,
  people,
  defaultPlanId,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  channels: CodeLabel[]
  formats: CodeLabel[]
  audiences: CodeLabel[]
  ctas: CodeLabel[]
  plans: MarketingPlan[]
  people: MarketingPerson[]
  defaultPlanId: string | null
  onCreated: (id: string) => void
}) {
  const create = useCreateContentItem()

  const [title, setTitle] = useState('')
  const [channel, setChannel] = useState('')
  const [format, setFormat] = useState('')
  const [planId, setPlanId] = useState('')
  const [caption, setCaption] = useState('')
  const [brief, setBrief] = useState('')
  const [scheduledFor, setScheduledFor] = useState('')
  const [owner, setOwner] = useState('')
  const [audience, setAudience] = useState('driver')
  const [cta, setCta] = useState('')
  const [designCopy, setDesignCopy] = useState('')
  const [productionNote, setProductionNote] = useState('')
  const [sourceBasis, setSourceBasis] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    setTitle('')
    setChannel(channels[0]?.code ?? '')
    setFormat(formats[0]?.code ?? '')
    setPlanId(defaultPlanId ?? '')
    setCaption('')
    setBrief('')
    setScheduledFor('')
    setOwner('')
    setAudience(audiences[0]?.code ?? 'driver')
    setCta('')
    setDesignCopy('')
    setProductionNote('')
    setSourceBasis('')
  }, [open, channels, formats, audiences, defaultPlanId])

  async function submit() {
    setBusy(true)
    try {
      const row = (await create.trigger({
        title,
        channel,
        format,
        plan_id: planId || null,
        caption: caption || null,
        brief: brief || null,
        scheduled_for: scheduledFor || null,
        audience,
        cta: cta || null,
        design_copy: designCopy || null,
        production_note: productionNote || null,
        source_basis: sourceBasis || null,
        // Empty means "me", decided in the database from the signed-in address,
        // so a piece of content with nobody carrying it cannot exist.
        owner_names: owner ? [owner] : [],
      }).result) as { id: string } | null
      toast.success('Added as a draft', {
        description: 'It is also on the task board now, so it shows up in the morning.',
      })
      onOpenChange(false)
      if (row?.id) onCreated(row.id)
    } catch (err) {
      toast.error('That was not created', { description: writeErrorMessage(err) })
    } finally {
      setBusy(false)
    }
  }

  const ready = title.trim() !== '' && channel !== '' && format !== ''

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New content</DialogTitle>
          <DialogDescription>
            This lands in Draft and creates a task carrying it, assigned to whoever you name.
            Nothing goes to the approver until you send it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="nc-title" className="text-xs">
              What is it
            </Label>
            <Textarea
              id="nc-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              rows={2}
              placeholder="Driver of the month, Sipho"
              className="text-sm"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Audience</Label>
              <NativeSelect
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                className="h-9"
              >
                {audiences.map((a) => (
                  <option key={a.code} value={a.code}>
                    {a.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Call to action</Label>
              <NativeSelect value={cta} onChange={(e) => setCta(e.target.value)} className="h-9">
                <option value="">Not set yet</option>
                {ctas.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Channel</Label>
              <NativeSelect
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                className="h-9"
              >
                {channels.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Format</Label>
              <NativeSelect
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                className="h-9"
              >
                {formats.map((f) => (
                  <option key={f.code} value={f.code}>
                    {f.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Plan</Label>
              <NativeSelect
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                className="h-9"
              >
                <option value="">Not in a plan</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nc-when" className="text-xs">
                Goes out on
              </Label>
              <Input
                id="nc-when"
                type="date"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Who is carrying it</Label>
              <NativeSelect
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                className="h-9"
              >
                <option value="">Me</option>
                {people.map((p) => (
                  <option key={p.email} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="nc-design" className="text-xs">
              The words that go on the artwork
            </Label>
            {/* Not the caption. Two different pieces of writing by two different
                people, kept in separate files by the plan for that reason. */}
            <Textarea
              id="nc-design"
              value={designCopy}
              onChange={(e) => setDesignCopy(e.target.value)}
              rows={3}
              placeholder="YOUR PAY HAS A FLOOR."
              className="font-mono text-xs"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="nc-source" className="text-xs">
              Figures come from
            </Label>
            <Input
              id="nc-source"
              value={sourceBasis}
              onChange={(e) => setSourceBasis(e.target.value)}
              placeholder="Salary & Shift Structure"
              className="h-9"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="nc-note" className="text-xs">
              The guardrail, if this post has one
            </Label>
            {/* Copied from the plan's production note, verbatim. It is shown at
                the top of the panel to whoever writes the caption and to whoever
                approves it. */}
            <Input
              id="nc-note"
              value={productionNote}
              onChange={(e) => setProductionNote(e.target.value)}
              placeholder="R1,200 is a target, not guaranteed pay"
              className="h-9"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="nc-caption" className="text-xs">
              A first caption option, if you have one
            </Label>
            <Textarea
              id="nc-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={3}
              className="text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="nc-brief" className="text-xs">
              Why this exists, for the approver
            </Label>
            <Textarea
              id="nc-brief"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              rows={2}
              placeholder="The audience, the offer, the reason it is going out now"
              className="text-sm"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="brand"
            size="sm"
            className="gap-1.5"
            disabled={!ready || busy}
            onClick={() => void submit()}
          >
            {busy && <Spinner className="h-3.5 w-3.5" />}
            Add as a draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
